import * as actions from '@actions';
import * as SurveyPDF from 'survey-pdf';
import lucyBanner from '@images/LucyBannerA4.jpg';
import _ from 'lodash';
import Auth from 'aws-amplify';
import { scaleImages } from '@components/SurveyViewer/scaleImages';
import previewUnavailable from '@images/previewUnavailable.png';
import uploadFailed from '@images/uploadFailed.png';
import uploadFileToS3 from '@helpers/s3';
import type { Dispatch } from 'react-redux';
import { getRuntimeConfig } from 'vl-common/src/hooks/Runtime';
// import { errorModal } from 'vl-common/src/components/Modals/CustomModals/Error';
import { convertDataUrlToFile, findAndSaveCroppedImages } from './findAndSaveCroppedImages';
import { setLicenseKey } from 'survey-core';

const formatFileName = (originalName: string, stack: number) => {
  const fileName = originalName.substring(0, originalName.lastIndexOf('.'));
  const fileFormat = originalName.split('.').pop();

  return `${fileName} (${stack}).${fileFormat}`;
};

/**
 * Returns a deep copy of jsonSurveyAnswers with embedded file answers transformed by updateFn. File answers have
 * "name" (string) "type" (mime type string) and "content" (string) properties.
 *
 * @param jsonSurveyAnswers the original survey answers, as JSON
 * @param updateFn applied to file elements, should destructively update its sole parameter. Can return a Promise.
 * @returns {Promise<string>} a copy of the jsonSurveyAnswers, suitably transformed
 */
const updateEmbeddedFiles = async (jsonSurveyAnswers, updateFn, surveyId = '') => {
  const embeddedFiles = [];
  const embeddedFileNames = [];

  function gatherEmbeddedFiles(node, originalName = null, stack = 0) {
    // Only operate on file answer objects
    const isFile =
      node &&
      typeof node === 'object' &&
      'name' in node &&
      ('content' in node || 'dataURL' in node || 'uploadedToS3' in node);

    if (!isFile) return undefined;

    if (surveyId && typeof node.name === 'string' && !node.name.includes(surveyId)) {
      node.name = surveyId + node.name;
    }

    if (embeddedFileNames.includes(node.name)) {
      const nameBeforeEditing = node.name;
      node.name = formatFileName(originalName || nameBeforeEditing, stack);
      return gatherEmbeddedFiles(node, originalName || nameBeforeEditing, stack + 1);
    }

    const copy = { ...node };
    embeddedFiles.push(copy);
    embeddedFileNames.push(node.name);
    return copy;
  }

  const answers = _.cloneDeepWith(jsonSurveyAnswers, (node) => gatherEmbeddedFiles(node, null, 0));

  await Promise.all(embeddedFiles.map((f) => updateFn(f)));

  return answers;
};

/**
 * Removes file "contents" elements. This would typically be invoked after embedded images have been saved but before the response
 * has been saved to the backend (since the backend fails if there are any data: URL images in the survey response).
 *
 * @param jsonSurveyAnswers
 * @returns {Promise<string>}
 */
const removeEmbeddedImages = async (jsonSurveyAnswers) => {
  function removeImages(element) {
    const { content, s3Filename, dataURL, hiRes } = element;

    // Either delete content field or set to S3 file link
    if (content?.startsWith('data:')) {
      if (s3Filename) {
        element.content = s3Filename;
      } else {
        delete element.content;
      }
    }
    // These will always be bas64 so just remove
    if (dataURL) delete element.dataURL;
    if (hiRes) delete element.hiRes;
  }

  return updateEmbeddedFiles(jsonSurveyAnswers, removeImages);
};

/**
 * Uploads new images in a Survey model to S3 if they have not previously been saved.
 *
 * @param model the SurveyJS model object
 * @param user_guid the patient user_guid
 * @param survey_id the id of the survey instance
 * @param dispatch dispatch function
 * @param appt_id ID for appointment
 * @returns {Promise<void>} resolves when all images have been uploaded
 */
const uploadEmbeddedImagesToS3 = async (model, user_guid, survey_id, dispatch, appt_id) => {
  const answers = model.data;

  async function uploadImageToS3(element) {
    const { name, content, uploadedToS3 } = element;
    if (!uploadedToS3 && content?.startsWith('data:')) {
      const res = await uploadDataToS3({
        dispatch,
        filename: name,
        survey_id,
        data: content,
        user_guid,
        filetype: undefined,
        logError: true,
        appt_id
      });

      if (res) {
        element.uploadedToS3 = true;
      }
    }
  }

  model.data = await updateEmbeddedFiles(answers, uploadImageToS3, survey_id);
};

const uploadImagesWithCropToS3 = async (
  images: any[],
  user_guid: string,
  survey_id: number,
  dispatch: Dispatch,
  userHasReadPermissions: boolean
) => {
  // loop through images
  // if no content then upload to s3
  // else don't upload
  // return with content field
  const uploadCroppedImageToS3 = async (image: any) => {
    const { name, content, dataURL, uploadedToS3 } = image;
    if ((!content && dataURL) || (!uploadedToS3 && content?.startsWith('data:'))) {
      // convert to file
      const imageAsFile = await convertDataUrlToFile(dataURL, name);

      const uploadRes = await uploadFileToS3(imageAsFile, dispatch, user_guid, userHasReadPermissions, survey_id);

      return {
        ...image,
        name: uploadRes.file.name,
        type: uploadRes.mimeType,
        content: uploadRes.url,
        uploadedToS3: true
      };
    }
    return image;
  };

  const uploadedImages = await Promise.all(images.map((image) => uploadCroppedImageToS3(image)));
  return uploadedImages;
};

const downloadFileFromS3 = async (user_guid, filename, dispatch) => {
  const { url: tempAWSUrl } = await dispatch(actions.getDocs({ user_guid, filename, tempurl: true }));

  if (!tempAWSUrl) {
    throw Error(`Could not locate file: "${tempAWSUrl}"`);
  }

  const r = await fetch(tempAWSUrl);
  const blob = await r.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getAWSFileUrl = async (user_guid, filename, dispatch) => {
  const { url } = await dispatch(actions.getDocs({ user_guid, filename, tempurl: true }));

  if (!url) {
    throw Error(`Could not locate file: "${url}"`);
  }

  return url;
};

export const imageToBase64 = async (image) => {
  const res = await fetch(image?.src || image);
  const blob = await res.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getSurveyImageFromS3 = _.memoize(
  async (name, user_guid, userHasReadPermissions, survey_id, dispatch, skipReplacement) => {
    let content;

    try {
      // this fails for Guest access users
      const response = await dispatch(
        actions.getDocs({ user_guid, filename: `${name}`, tempurl: true }),
        userHasReadPermissions
      );
      const awsTempUrl = response.url;

      if (awsTempUrl) {
        content = await imageToBase64(awsTempUrl);
      } else {
        content = await imageToBase64(previewUnavailable);
      }

      // At this point content is the file downloaded from S3.
      // We used to always replace non-image content with a placeholder
      // But sometimes we actually want to download & keep the file.
      // So skipReplacement === true avoids actually replacing the file with a placeholder
      if ((!content.startsWith('data:image/') || !userHasReadPermissions) && !skipReplacement) {
        content = await imageToBase64(previewUnavailable);
      }
    } catch {
      content = await imageToBase64(uploadFailed);
    }

    return content as string;
  }
);

// This function replaces S3 links with the actual image via a function called getSurveyImageFromS3
// It also calls a function that loops through embedded files to replace them with the actual data
// This function used to always replaces non-image files with a placeholder
// But sometimes we want to keep the actual file e.g when saving/completing a survey
const inlineResponseImages = async (
  jsonSurveyAnswers,
  user_guid,
  userHasReadPermissions,
  survey_id,
  dispatch,
  skipReplacement = false
) => {
  const retrieveImageFromS3 = async (fileElement) => {
    const { name, content } = fileElement;

    if (content?.startsWith('data:')) return;

    fileElement.s3Filename = content;
    fileElement.uploadedToS3 = true;
    // This call here is what actually replaces the content. skipReplacement ensures that non-image files are kept in full
    fileElement.content = await getSurveyImageFromS3(
      name,
      user_guid,
      userHasReadPermissions,
      survey_id,
      dispatch,
      skipReplacement
    );
  };

  return updateEmbeddedFiles(jsonSurveyAnswers, retrieveImageFromS3);
};

const makeSurveyPDF = async (response, definition, surveyjsLicenseKey?: string, logProgress = console.log) => {
  const options = {
    commercial: true,
    orientation: 'p',
    fontSize: 11,
    margins: {
      left: 25.4,
      right: 25.4,
      top: 35,
      bot: 25.4
    },
    format: 'a4',
    htmlRenderAs: 'standard',
    matrixRenderAs: 'auto',
    compress: true
  };
  // @ts-ignore
  if (surveyjsLicenseKey) {
    setLicenseKey(surveyjsLicenseKey);
  }

  const surveyPDF = new SurveyPDF.SurveyPDF(definition, options);
  surveyPDF.data = response;
  surveyPDF.mode = 'display'; // Create non-editable pdfs
  surveyPDF.onRenderHeader.add((_, canvas) => {
    canvas.drawImage({
      base64: lucyBanner.blurDataURL || '',
      rect: {
        xLeft: 0,
        // @ts-ignore
        xRight: canvas.controller.paperWidth,
        yTop: 0,
        yBot: 72
      }
    });
  });

  surveyPDF.onRenderQuestion.add(async (_, opt: any) => {
    if (opt.question.getType() === 'file' && opt.question.previewValue?.length > 0) {
      const stripLinks = (brick: any) => {
        if (!brick) return;

        if (Array.isArray(brick?.bricks)) {
          brick.bricks.forEach(stripLinks);
        } else {
          delete brick.url;
          delete brick.link;
          delete brick.onClick;
        }
      };

      opt.bricks?.forEach(stripLinks);
    }
  });

  surveyPDF.onRenderFooter.add((survey, canvas) => {
    canvas.drawText({
      text: 'Service provided by HBSUK',
      fontSize: 10,
      // @ts-ignore
      horizontalAlign: 'right',
      // @ts-ignore
      verticalAlign: 'bottom',
      margins: {
        right: 26,
        bot: 26
      }
    });
  });
  let callCount = 1;

  surveyPDF.onRenderQuestion.unshift((survey, event) => {
    const msg = `Processing Question: ${callCount++}/${event.question.survey.getAllQuestions(true).length}, ${
      event.question.name
    }`;

    console.log(msg);
    logProgress(msg);
  });

  return surveyPDF;
};

function toBase64(data) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(data);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

async function uploadDataToS3({
  dispatch,
  filename,
  survey_id,
  data,
  user_guid,
  filetype,
  setCompletingPercent = (percent: number) => {},
  logError = false,
  appt_id = ''
}) {
  const uploadedFileType = filetype ? { filetype } : {};

  function uploadDocs(args) {
    return dispatch(actions.uploadDocs({ user_guid, filename, survey_id, ...uploadedFileType, ...args }));
  }
  try {
    // @ts-ignore
    const base64 = data?.startsWith?.('data:') ? data.split(',')[1] : (await toBase64(data)).split(',')[1];

    // request temporary upload URL
    const { payload } = await uploadDocs({ base64: base64.slice(0, 8 * 1024), tempurl: true });

    setCompletingPercent(70);

    if (payload.url) {
      const blob = await (await fetch(`data:${payload.mime};base64,${base64}`)).blob();
      const response = await fetch(payload.url, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': payload.mime,
          'Content-Disposition': 'inline',
          redirect: 'follow'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to upload file to S3');
      }

      setCompletingPercent(80);

      // acknowledge file uploaded
      const response2 = await uploadDocs({ uploaded: true });
      setCompletingPercent(90);

      // todo - check return status
      console.log({ response, response2 });

      return payload.url;
    }

    return null;
  } catch (error) {
    console.error('Error in uploadDataToS3: ', error);
    console.error('Failed to upload data to S3. Continuing...');

    if (logError) {
      log({
        user_guid,
        appt_id,
        logs: [
          {
            sequenceNumber: 0,
            logLevel: 'ERROR',
            message: JSON.stringify(
              ['vlm-1852-document-upload-error', error, { ...filename, survey_id, data, filetype }],
              errorReplacer
            ),
            timestampMs: new Date().getTime()
          }
        ]
      });
    }

    setCompletingPercent(90);
    return null;
  }
}

// Override SurveyPDF.SurveyHelper.getImageSize to down scale the images which were previously upscaled in resizeImage(). This mechanism renders images at 144dpi rather than 72dpi
async function generatePdf(surveyPdf: SurveyPDF.SurveyPDF) {
  const oldGetImagesize = SurveyPDF.SurveyHelper.getImageSize;
  try {
    SurveyPDF.SurveyHelper.getImageSize = async (...args) => {
      const { width, height } = await oldGetImagesize(...args);
      return { width: width / 2, height: height / 2 };
    };
    const pdfblob = await surveyPdf.raw('blob');
    return pdfblob;
  } catch (e) {
    console.error(e);
    throw e;
  } finally {
    SurveyPDF.SurveyHelper.getImageSize = oldGetImagesize;
  }
}

const toBase64URLDataEncoded = async (file: string) => {
  const imageBlob = await (await fetch(file)).blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(imageBlob);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

// destructively modifies the given surveyDefinition by replacing image URLs with data: URLs.
const inlineQuestionImages = async (surveyDefinition) => {
  const objectsWithImageLinks = [];
  function walker(key: string, value: unknown) {
    if (key === 'imageLink' && typeof value === 'string' && RegExp('^https?//').test(value)) {
      objectsWithImageLinks.push(this);
    }
    return value;
  }

  // a "poor man's" visitor - the walker gathers up all the objects with imageLink attributes
  JSON.stringify(surveyDefinition, walker);

  // replace all the imageLinks with data encoded URL strings
  await Promise.all(
    objectsWithImageLinks.map((o) => toBase64URLDataEncoded(o.imageLink).then((img) => (o.imageLink = img)))
  );
};

/**
 * If the file objects "contents" is a data URL then save it to S3. Otherwise download the file as a data URL. Generate and save the PDF to S3.
 * Return the JSON answers with no content property.
 *
 * @param originalAnswers
 * @param definition
 * @param surveyObject - HBS Survey Object, as returned by actions.getAssessmentSurvey()
 * @param file
 * @param dispatch
 * @param case_id
 * @param setCompletingPercent
 * @param setProgressMessage
 * @param setModalVisible
 * @returns {Promise<void>}
 */
const saveSurveyToS3 = async (
  originalAnswers: unknown,
  definition: unknown,
  surveyObject: {
    survey_id: number;
    patient_user_guid: string;
    cat_code: string;
  },
  file: string,
  dispatch: Dispatch,
  case_id?: number | null,
  surveyjsLicenseKey?: string,
  setCompletingPercent = (val: number) => {},
  setProgressMessage = (msg: string) => {},
  setModalVisible = (val: boolean) => {}
) => {
  function flush() {
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  // simplest way to suppress errors whilst saving
  function errorModal(props) {}

  const closeProgressModal = () => {
    setModalVisible(false);
    setCompletingPercent(0);
    setProgressMessage('');
  };
  const { survey_id, patient_user_guid: user_guid, userIsRestricted = false } = surveyObject;
  setProgressMessage('Formatting answers');
  await flush();
  // New 'true' arg to inlineResponseImages stops non-image content being replaced by placeholder images
  // We still need to upload the actual content!
  const inlinedAnswers = await inlineResponseImages(
    JSON.parse(JSON.stringify(originalAnswers)),
    user_guid,
    !userIsRestricted,
    survey_id,
    dispatch,
    true
  );
  setCompletingPercent(30);
  setProgressMessage('Scaling images');
  await flush();
  // updates both the responses and the definition
  const { responses, definition: def } = await scaleImages(inlinedAnswers, definition, surveyObject, case_id, dispatch);

  await findAndSaveCroppedImages(inlinedAnswers, definition, surveyObject, dispatch, !userIsRestricted, case_id);
  setCompletingPercent(40);
  setProgressMessage('Building PDF generator');
  await inlineQuestionImages(def);
  await flush();

  const withHandleSurveyError = async <T>(fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn();
    } catch (e) {
      console.error('Survey error:', e);

      try {
        throwSurveyError(def);
        errorModal({
          title: 'Failed generating PDF',
          content: 'Failed to generate PDF. Please try again. If the problem persists, please contact support.'
        });
      } catch (surveyError) {
        errorModal({
          title: 'Failed generating PDF',
          // @ts-expect-error
          content: surveyError?.message
        });
      }

      closeProgressModal();
      return Promise.reject();
    }
  };

  const surveyPDF = await withHandleSurveyError(() =>
    makeSurveyPDF(responses, def, surveyjsLicenseKey, async (s: string) => {
      setProgressMessage(s);
      await flush();
    })
  );

  setCompletingPercent(50);
  setProgressMessage('Generating PDF');
  await flush();
  const pdfblob = await withHandleSurveyError(() => generatePdf(surveyPDF));
  setProgressMessage('PDF Generation complete');
  await flush();
  setCompletingPercent(60);

  if (pdfblob.size / 1_000_000 > getRuntimeConfig().MAX_SURVEY_SIZE_MB) {
    errorModal({
      title: 'Document too large',
      content: 'Sorry, this document is too large, try removing some files'
    });
    closeProgressModal();
    return Promise.reject();
  }
  const filename = `surveyjs.${survey_id}.pdf`;

  setProgressMessage('Uploading PDF to server');
  await flush();
  return uploadDataToS3({
    dispatch,
    filename,
    survey_id,
    data: pdfblob,
    user_guid,
    filetype: 1,
    setCompletingPercent
  })
    .catch((_) => {
      errorModal({
        title: 'Failed to save survey',
        content: 'Failed to save survey. Please try again. If the problem persists, please contact support.'
      });
      closeProgressModal();
      return Promise.reject();
    })
    .finally(async () => {
      setProgressMessage('PDF upload complete');
      await flush();
    });
};

export function throwSurveyError(surveyJson: any) {
  if (!surveyJson?.pages) return;

  for (const page of surveyJson.pages) {
    for (const question of page.elements || []) {
      try {
        // Test if this question might be the culprit
        new SurveyPDF.SurveyPDF({ pages: [{ elements: [question] }] }, {});
      } catch (e) {
        const e2 = Error(
          `PDF generation failed on question '${question.name}' (a ${question.type}) ` +
            `on page '${page.name}'. Original error: ${e.message}`
        );

        throw e2;
      }
    }
  }
}

const log = async (body: any) => {
  const currentSession = await Auth.currentSession();

  return fetch(`${getRuntimeConfig().API_URL}/logs/test`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${currentSession.getIdToken().getJwtToken()}`
    }
  });
};

const errorReplacer = (key, value) => {
  if (value instanceof Error) {
    return {
      ...value,
      name: value.name,
      message: value.message,
      stack: value.stack // Optional: remove if you don't want stack traces in logs
    };
  }
  return value;
};

// consume and log failures to CloudWatch under vlm-1774
const failsafeSaveSurveyToS3: typeof saveSurveyToS3 = async (...args) => {
  const [, , surveyObject, , , , , setCompletingPercent, setProgressMessage, setModalVisible] = args;
  const { appt_id, patient_user_guid: user_guid } = surveyObject;

  await saveSurveyToS3(...args).catch((e) => {
    log({
      user_guid,
      appt_id,
      logs: [
        {
          sequenceNumber: 0,
          logLevel: 'ERROR',
          message: JSON.stringify(['vlm-1774-survey-saving-error', e, args], errorReplacer),
          timestampMs: new Date().getTime()
        }
      ]
    });
    // these settings allow the user to click OK and return to their dashboard
    setModalVisible?.(true);
    setCompletingPercent?.(100);
    setProgressMessage?.('');

    return new Promise((resolve) => setTimeout(resolve, 0));
  });

  return null;
};

export {
  updateEmbeddedFiles,
  removeEmbeddedImages,
  uploadEmbeddedImagesToS3,
  uploadImagesWithCropToS3,
  getSurveyImageFromS3,
  downloadFileFromS3,
  inlineResponseImages,
  makeSurveyPDF,
  getAWSFileUrl,
  failsafeSaveSurveyToS3 as saveSurveyToS3
};
